import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { IconX } from '@tabler/icons-react';
import * as api from '../../api';
import './CustomerPicker.scss';

const SEARCH_DEBOUNCE_MS = 300;

export default function CustomerPicker({
    label = 'Клиент',
    value = null,
    onChange,
    error,
    disabled = false,
    placeholder = 'Введите логин...',
    inline = false,
    className = '',
    minWidth,
}) {
    const listId = useId();
    const rootRef = useRef(null);
    const [query, setQuery] = useState(value?.login || '');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [searchError, setSearchError] = useState('');

    useEffect(() => {
        setQuery(value?.login || '');
    }, [value?.id, value?.login]);

    useEffect(() => {
        if (!open) return undefined;

        const handlePointerDown = (event) => {
            if (!rootRef.current?.contains(event.target)) {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handlePointerDown);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
        };
    }, [open]);

    const runSearch = useCallback(async (searchQuery) => {
        const trimmed = searchQuery.trim();

        if (!trimmed) {
            setOptions([]);
            setSearchError('');
            return;
        }

        setLoading(true);
        setSearchError('');

        try {
            const data = await api.searchCustomers(trimmed);
            setOptions(data.items || []);
        } catch (err) {
            setOptions([]);
            setSearchError(err.message || 'Ошибка поиска');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (value && query === value.login) {
            return undefined;
        }

        const timer = window.setTimeout(() => {
            if (open) {
                runSearch(query);
            }
        }, SEARCH_DEBOUNCE_MS);

        return () => {
            window.clearTimeout(timer);
        };
    }, [query, open, value, runSearch]);

    const handleInputChange = (event) => {
        const nextQuery = event.target.value;
        setQuery(nextQuery);
        setOpen(true);

        if (value && nextQuery !== value.login) {
            onChange?.(null);
        }
    };

    const handleSelect = (customer) => {
        onChange?.({ id: customer.id, login: customer.login });
        setQuery(customer.login);
        setOpen(false);
        setOptions([]);
    };

    const handleClear = () => {
        onChange?.(null);
        setQuery('');
        setOptions([]);
        setOpen(false);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            setOpen(false);
        }
    };

    const wrapClass = [
        'ui-customer-picker__input-wrap',
        error && 'ui-customer-picker__input-wrap--error',
        (value || query) && 'ui-customer-picker__input-wrap--with-action',
    ].filter(Boolean).join(' ');

    const rootClass = [
        'ui-customer-picker',
        inline && 'ui-customer-picker--inline',
        className,
    ].filter(Boolean).join(' ');

    const style = minWidth ? { minWidth } : undefined;

    return (
        <label className={rootClass} ref={rootRef} style={style}>
            {label && <span className="ui-customer-picker__label">{label}</span>}
            <div className={wrapClass}>
                <input
                    className="ui-customer-picker__input"
                    type="text"
                    value={query}
                    onChange={handleInputChange}
                    onFocus={() => setOpen(true)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    disabled={disabled}
                    role="combobox"
                    aria-expanded={open}
                    aria-controls={listId}
                    autoComplete="off"
                    spellCheck={false}
                />
                {(value || query) && !disabled && (
                    <div className="ui-customer-picker__action">
                        <button
                            type="button"
                            className="ui-customer-picker__clear"
                            onClick={handleClear}
                            aria-label="Очистить"
                        >
                            <IconX size={16} stroke={1.5} />
                        </button>
                    </div>
                )}
                {open && query.trim() && (
                    <ul className="ui-customer-picker__dropdown" id={listId} role="listbox">
                        {loading && (
                            <li className="ui-customer-picker__option ui-customer-picker__option--muted">
                                Поиск...
                            </li>
                        )}
                        {!loading && searchError && (
                            <li className="ui-customer-picker__option ui-customer-picker__option--muted">
                                {searchError}
                            </li>
                        )}
                        {!loading && !searchError && options.length === 0 && (
                            <li className="ui-customer-picker__option ui-customer-picker__option--muted">
                                Ничего не найдено
                            </li>
                        )}
                        {!loading && !searchError && options.map((customer) => (
                            <li key={customer.id}>
                                <button
                                    type="button"
                                    className="ui-customer-picker__option"
                                    role="option"
                                    aria-selected={value?.id === customer.id}
                                    onClick={() => handleSelect(customer)}
                                >
                                    {customer.login}
                                    {' '}
                                    <span className="ui-customer-picker__option-id">
                                        (ID:
                                        {' '}
                                        {customer.id}
                                        )
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            {error && <span className="ui-customer-picker__error">{error}</span>}
        </label>
    );
}
